import React, { useState, useEffect } from 'react';
import {
    Box,
    Paper,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    TablePagination,
    Tabs,
    Tab,
    Stack,
    Button,
    Menu,
    MenuItem,
    IconButton,
} from '@mui/material';
import {
    Assessment as AssessmentIcon,
    Download as DownloadIcon,
    CardGiftcard as CardGiftcardIcon,
    TrendingUp as TrendingUpIcon,
    People as PeopleIcon,
    PieChart as PieChartIcon,
    BarChart as BarChartIcon,
} from '@mui/icons-material';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { getRewardGiveoutReport, downloadRewardGiveoutReport, getTopSpenderReport, downloadTopSpenderReport, getAgeDistributionReport } from '../services/reportService';
import apiClient from '../config/api';
import { TextField, ToggleButton, ToggleButtonGroup, CircularProgress } from '@mui/material';
import {
    PieChart,
    Pie,
    Cell,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer
} from 'recharts';

const MemberReport = () => {
    // Tab state
    const [tabValue, setTabValue] = useState(0);
    
    // State for date range - default to last 30 days
    const [startDate, setStartDate] = useState(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000));
    const [endDate, setEndDate] = useState(new Date());

    // State for Reward Giveout Report
    const [rewards, setRewards] = useState([]);
    const [staffNames, setStaffNames] = useState({}); // Map of email -> name
    const [sortBy, setSortBy] = useState('RewardHandoutDate');
    const [sortOrder, setSortOrder] = useState('DESC');
    
    // Pagination state
    const [page, setPage] = useState(0); // MUI uses 0-based pages
    const [pageSize, setPageSize] = useState(10);
    const [totalCount, setTotalCount] = useState(0);
    const [loading, setLoading] = useState(false);

    // State for Top Spender Report
    const [topSpenderStartDate, setTopSpenderStartDate] = useState(new Date(Date.now() - 365 * 24 * 60 * 60 * 1000)); // 1 year ago
    const [topSpenderEndDate, setTopSpenderEndDate] = useState(new Date());
    const [topN, setTopN] = useState(10);
    const [topSpenders, setTopSpenders] = useState([]);
    const [topSpenderLoading, setTopSpenderLoading] = useState(false);
    const [topSpenderExportAnchorEl, setTopSpenderExportAnchorEl] = useState(null);

    // State for Age Distribution Report
    const [ageDistribution, setAgeDistribution] = useState([]);
    const [ageDistributionLoading, setAgeDistributionLoading] = useState(false);
    const [chartType, setChartType] = useState('pie'); // 'pie' or 'bar'

    // State for export menu
    const [exportAnchorEl, setExportAnchorEl] = useState(null);

    // Fetch staff names from API
    const fetchStaffNames = async (emails) => {
        if (emails.length === 0) return {};
        
        try {
            const response = await apiClient.post('/api/auth/user-names', {
                emails: emails
            });
            return response.data.names || {};
        } catch (error) {
            console.error('Failed to fetch staff names:', error);
            // Return email as fallback
            const fallback = {};
            emails.forEach(email => {
                fallback[email] = email;
            });
            return fallback;
        }
    };

    // Load data
    const loadRewards = async () => {
        try {
            setLoading(true);
            // Set start date to beginning of day to include all records on that date
            const startOfDay = new Date(startDate);
            startOfDay.setHours(0, 0, 0, 0);
            
            // Set end date to end of day to include all records on that date
            const endOfDay = new Date(endDate);
            endOfDay.setHours(23, 59, 59, 999);
            
            const response = await getRewardGiveoutReport(
                startOfDay, 
                endOfDay, 
                sortBy, 
                sortOrder,
                page + 1, // Convert to 1-based for API
                pageSize
            );
            
            const rewardsData = response.rewards || [];
            setRewards(rewardsData);
            setTotalCount(response.pagination?.totalCount || 0);
            
            // Extract unique staff emails and fetch their names
            const uniqueStaffEmails = [...new Set(rewardsData.map(r => r.Staff).filter(Boolean))];
            if (uniqueStaffEmails.length > 0) {
                const names = await fetchStaffNames(uniqueStaffEmails);
                setStaffNames(prev => ({ ...prev, ...names }));
            }
        } catch (error) {
            console.error('Failed to load reward giveout report:', error);
            // TODO: Add error notification
        } finally {
            setLoading(false);
        }
    };

    // Handle page change
    const handlePageChange = (event, newPage) => {
        setPage(newPage);
    };

    // Handle rows per page change
    const handleChangeRowsPerPage = (event) => {
        setPageSize(parseInt(event.target.value, 10));
        setPage(0);
    };

    // Effect to load data when dependencies change
    useEffect(() => {
        loadRewards();
    }, [startDate, endDate, sortBy, sortOrder, page, pageSize]);

    // Handle sort change
    const handleSort = (column) => {
        const isAsc = sortBy === column && sortOrder === 'ASC';
        setSortOrder(isAsc ? 'DESC' : 'ASC');
        setSortBy(column);
        setPage(0); // Reset to first page when sorting
    };

    // Handle export menu
    const handleExportClick = (event) => {
        setExportAnchorEl(event.currentTarget);
    };

    const handleExportClose = () => {
        setExportAnchorEl(null);
    };

    const handleExport = async (format) => {
        try {
            // Set start date to beginning of day to include all records on that date
            const startOfDay = new Date(startDate);
            startOfDay.setHours(0, 0, 0, 0);
            
            // Set end date to end of day to include all records on that date
            const endOfDay = new Date(endDate);
            endOfDay.setHours(23, 59, 59, 999);
            
            await downloadRewardGiveoutReport(startOfDay, endOfDay, format);
        } catch (error) {
            console.error(`Failed to export as ${format}:`, error);
            // TODO: Add error notification
        }
        handleExportClose();
    };

    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
    };

    // Load Top Spender data
    const loadTopSpenders = async () => {
        try {
            setTopSpenderLoading(true);
            // Set start date to beginning of day
            const startOfDay = new Date(topSpenderStartDate);
            startOfDay.setHours(0, 0, 0, 0);
            
            // Set end date to end of day
            const endOfDay = new Date(topSpenderEndDate);
            endOfDay.setHours(23, 59, 59, 999);
            
            const response = await getTopSpenderReport(startOfDay, endOfDay, topN);
            setTopSpenders(response.topSpenders || []);
        } catch (error) {
            console.error('Failed to load top spender report:', error);
            // TODO: Add error notification
        } finally {
            setTopSpenderLoading(false);
        }
    };

    // Effect to load Top Spender data when dependencies change
    useEffect(() => {
        if (tabValue === 1) {
            loadTopSpenders();
        }
    }, [topSpenderStartDate, topSpenderEndDate, topN, tabValue]);

    // Load Age Distribution data
    const loadAgeDistribution = async () => {
        try {
            setAgeDistributionLoading(true);
            const response = await getAgeDistributionReport();
            setAgeDistribution(response.ageDistribution || []);
        } catch (error) {
            console.error('Failed to load age distribution report:', error);
            // TODO: Add error notification
        } finally {
            setAgeDistributionLoading(false);
        }
    };

    // Effect to load Age Distribution data when tab is selected
    useEffect(() => {
        if (tabValue === 2) {
            loadAgeDistribution();
        }
    }, [tabValue]);

    // Handle Top Spender export menu
    const handleTopSpenderExportClick = (event) => {
        setTopSpenderExportAnchorEl(event.currentTarget);
    };

    const handleTopSpenderExportClose = () => {
        setTopSpenderExportAnchorEl(null);
    };

    const handleTopSpenderExport = async (format) => {
        try {
            // Set start date to beginning of day
            const startOfDay = new Date(topSpenderStartDate);
            startOfDay.setHours(0, 0, 0, 0);
            
            // Set end date to end of day
            const endOfDay = new Date(topSpenderEndDate);
            endOfDay.setHours(23, 59, 59, 999);
            
            await downloadTopSpenderReport(startOfDay, endOfDay, topN, format);
        } catch (error) {
            console.error(`Failed to export as ${format}:`, error);
            // TODO: Add error notification
        }
        handleTopSpenderExportClose();
    };

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AssessmentIcon color="primary" />
                Member Report
            </Typography>

            {/* Tabs */}
            <Paper sx={{ width: '100%', mb: 2, overflow: 'hidden' }}>
                <Tabs 
                    value={tabValue} 
                    onChange={handleTabChange} 
                    aria-label="member report tabs"
                    variant="scrollable"
                    scrollButtons="auto"
                    allowScrollButtonsMobile
                >
                    <Tab 
                        label="Reward Giveout Report" 
                        icon={<CardGiftcardIcon />} 
                        iconPosition="start"
                    />
                    <Tab 
                        label="Top Spender" 
                        icon={<TrendingUpIcon />} 
                        iconPosition="start"
                    />
                    <Tab 
                        label="Age Distribution" 
                        icon={<PeopleIcon />} 
                        iconPosition="start"
                    />
                </Tabs>
            </Paper>

            {/* Tab 0: Reward Giveout Report */}
            {tabValue === 0 && (
                <Box>
                    {/* Date Range and Export Controls */}
                    <Paper sx={{ p: 2, mb: 2 }}>
                        <Stack direction="row" spacing={2} alignItems="center">
                            <DesktopDatePicker
                                label="Start Date"
                                value={startDate}
                                onChange={setStartDate}
                                slotProps={{ textField: { variant: 'outlined' } }}
                            />
                            <DesktopDatePicker
                                label="End Date"
                                value={endDate}
                                onChange={setEndDate}
                                slotProps={{ textField: { variant: 'outlined' } }}
                            />
                            <Box sx={{ flexGrow: 1 }} />
                            <Button
                                variant="contained"
                                startIcon={<DownloadIcon />}
                                onClick={handleExportClick}
                            >
                                Export
                            </Button>
                        </Stack>
                    </Paper>

                    {/* Export Menu */}
                    <Menu
                        anchorEl={exportAnchorEl}
                        open={Boolean(exportAnchorEl)}
                        onClose={handleExportClose}
                    >
                        <MenuItem onClick={() => handleExport('excel')}>Excel</MenuItem>
                        <MenuItem onClick={() => handleExport('pdf')}>PDF</MenuItem>
                    </Menu>

                    {/* Rewards Table */}
                    <TableContainer component={Paper}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>
                                        <TableSortLabel
                                            active={sortBy === 'MembershipId'}
                                            direction={sortBy === 'MembershipId' ? sortOrder.toLowerCase() : 'asc'}
                                            onClick={() => handleSort('MembershipId')}
                                        >
                                            Membership Id
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell>
                                        <TableSortLabel
                                            active={sortBy === 'Reward'}
                                            direction={sortBy === 'Reward' ? sortOrder.toLowerCase() : 'asc'}
                                            onClick={() => handleSort('Reward')}
                                        >
                                            Reward
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell>
                                        <TableSortLabel
                                            active={sortBy === 'RewardHandoutDate'}
                                            direction={sortBy === 'RewardHandoutDate' ? sortOrder.toLowerCase() : 'asc'}
                                            onClick={() => handleSort('RewardHandoutDate')}
                                        >
                                            Date
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell>
                                        <TableSortLabel
                                            active={sortBy === 'Description'}
                                            direction={sortBy === 'Description' ? sortOrder.toLowerCase() : 'asc'}
                                            onClick={() => handleSort('Description')}
                                        >
                                            Description
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell>
                                        <TableSortLabel
                                            active={sortBy === 'Staff'}
                                            direction={sortBy === 'Staff' ? sortOrder.toLowerCase() : 'asc'}
                                            onClick={() => handleSort('Staff')}
                                        >
                                            Staff
                                        </TableSortLabel>
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {loading ? (
                                    <TableRow>
                                        <TableCell colSpan={5} align="center">
                                            Loading...
                                        </TableCell>
                                    </TableRow>
                                ) : rewards.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={5} align="center">
                                            No records found
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    rewards.map((reward, index) => (
                                        <TableRow key={`${reward.MembershipId}-${reward.Date}-${index}`}>
                                            <TableCell>{reward.MembershipId}</TableCell>
                                            <TableCell>{reward.Reward}</TableCell>
                                            <TableCell>
                                                {reward.Date 
                                                    ? new Date(reward.Date).toLocaleString() 
                                                    : '-'}
                                            </TableCell>
                                            <TableCell>{reward.Description || '-'}</TableCell>
                                            <TableCell>{staffNames[reward.Staff] || reward.Staff}</TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        component="div"
                        count={totalCount}
                        page={page}
                        onPageChange={handlePageChange}
                        rowsPerPage={pageSize}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        rowsPerPageOptions={[10, 25, 50]}
                        labelDisplayedRows={({ from, to, count }) => 
                            `${from}-${to} of ${count !== -1 ? count : 'more than ' + to}`}
                        showFirstButton
                        showLastButton
                        disabled={totalCount === 0}
                    />
                </Box>
            )}

            {/* Tab 1: Top Spender Report */}
            {tabValue === 1 && (
                <Box>
                    {/* Date Range and Top N Controls */}
                    <Paper sx={{ p: 2, mb: 2 }}>
                        <Stack direction="row" spacing={2} alignItems="center">
                            <DesktopDatePicker
                                label="Start Date"
                                value={topSpenderStartDate}
                                onChange={setTopSpenderStartDate}
                                slotProps={{ textField: { variant: 'outlined' } }}
                            />
                            <DesktopDatePicker
                                label="End Date"
                                value={topSpenderEndDate}
                                onChange={setTopSpenderEndDate}
                                slotProps={{ textField: { variant: 'outlined' } }}
                            />
                            <TextField
                                label="# of Records Return"
                                type="number"
                                value={topN}
                                onChange={(e) => setTopN(parseInt(e.target.value) || 10)}
                                inputProps={{ min: 1, max: 100 }}
                                sx={{ width: 180 }}
                                variant="outlined"
                            />
                            <Box sx={{ flexGrow: 1 }} />
                            <Button
                                variant="contained"
                                startIcon={<DownloadIcon />}
                                onClick={handleTopSpenderExportClick}
                            >
                                Export
                            </Button>
                        </Stack>
                    </Paper>

                    {/* Export Menu */}
                    <Menu
                        anchorEl={topSpenderExportAnchorEl}
                        open={Boolean(topSpenderExportAnchorEl)}
                        onClose={handleTopSpenderExportClose}
                    >
                        <MenuItem onClick={() => handleTopSpenderExport('excel')}>Excel</MenuItem>
                        <MenuItem onClick={() => handleTopSpenderExport('pdf')}>PDF</MenuItem>
                    </Menu>

                    {/* Top Spenders Table */}
                    <TableContainer component={Paper}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Member No</TableCell>
                                    <TableCell>Member Name</TableCell>
                                    <TableCell>Tier</TableCell>
                                    <TableCell align="right">Total Spend</TableCell>
                                    <TableCell>City</TableCell>
                                    <TableCell>Joined Date</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {topSpenderLoading ? (
                                    <TableRow>
                                        <TableCell colSpan={6} align="center">
                                            Loading...
                                        </TableCell>
                                    </TableRow>
                                ) : topSpenders.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={6} align="center">
                                            No records found
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    topSpenders.map((spender, index) => (
                                        <TableRow key={`${spender.MemberNo}-${index}`}>
                                            <TableCell>{spender.MemberNo}</TableCell>
                                            <TableCell>{spender.FirstName} {spender.LastName}</TableCell>
                                            <TableCell>{spender.Tier || 'No Tier'}</TableCell>
                                            <TableCell align="right">
                                                ${spender.TotalSpend ? parseFloat(spender.TotalSpend).toFixed(2) : '0.00'}
                                            </TableCell>
                                            <TableCell>{spender.City || '-'}</TableCell>
                                            <TableCell>
                                                {spender.JoinedDate 
                                                    ? new Date(spender.JoinedDate).toLocaleDateString() 
                                                    : '-'}
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>
            )}

            {/* Tab 2: Age Distribution Report */}
            {tabValue === 2 && (
                <Box>
                    {/* Chart Type Selection */}
                    <Paper sx={{ p: 2, mb: 2 }}>
                        <Stack direction="row" spacing={2} alignItems="center">
                            <Typography variant="body1" sx={{ minWidth: 'fit-content' }}>
                                Chart Type:
                            </Typography>
                            <ToggleButtonGroup
                                value={chartType}
                                exclusive
                                onChange={(event, newValue) => {
                                    if (newValue !== null) {
                                        setChartType(newValue);
                                    }
                                }}
                                aria-label="chart type"
                            >
                                <ToggleButton value="pie" aria-label="pie chart">
                                    <PieChartIcon sx={{ mr: 1 }} />
                                    Pie Chart
                                </ToggleButton>
                                <ToggleButton value="bar" aria-label="bar chart">
                                    <BarChartIcon sx={{ mr: 1 }} />
                                    Bar Chart
                                </ToggleButton>
                            </ToggleButtonGroup>
                        </Stack>
                    </Paper>

                    {/* Chart Display */}
                    {ageDistributionLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
                            <CircularProgress />
                        </Box>
                    ) : ageDistribution.length === 0 ? (
                        <Paper sx={{ p: 3, textAlign: 'center' }}>
                            <Typography variant="body1" color="text.secondary">
                                No age distribution data available. Members need to have Date of Birth set.
                            </Typography>
                        </Paper>
                    ) : (
                        <Paper sx={{ p: 3 }}>
                            <Typography variant="h6" gutterBottom>
                                Age Distribution
                            </Typography>
                            <Box sx={{ height: 500, mt: 3 }}>
                                <ResponsiveContainer width="100%" height="100%">
                                    {chartType === 'pie' ? (
                                        <PieChart>
                                            <Pie
                                                data={ageDistribution}
                                                dataKey="MemberCount"
                                                nameKey="AgeRange"
                                                cx="50%"
                                                cy="50%"
                                                outerRadius={150}
                                                label={({ AgeRange, MemberCount, percent }) => 
                                                    `${AgeRange}: ${MemberCount} (${(percent * 100).toFixed(1)}%)`
                                                }
                                            >
                                                {ageDistribution.map((entry, index) => (
                                                    <Cell 
                                                        key={`cell-${index}`} 
                                                        fill={['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d', '#ffc658', '#ff7300'][index % 8]} 
                                                    />
                                                ))}
                                            </Pie>
                                            <Tooltip 
                                                formatter={(value, name, props) => [
                                                    `${value} members`,
                                                    props.payload.AgeRange
                                                ]}
                                            />
                                            <Legend />
                                        </PieChart>
                                    ) : (
                                        <BarChart data={ageDistribution}>
                                            <CartesianGrid strokeDasharray="3 3" />
                                            <XAxis 
                                                dataKey="AgeRange" 
                                                angle={-45}
                                                textAnchor="end"
                                                height={100}
                                            />
                                            <YAxis 
                                                label={{ value: 'Number of Members', angle: -90, position: 'insideLeft' }}
                                                allowDecimals={false}
                                            />
                                            <Tooltip 
                                                formatter={(value) => [`${value} members`, 'Count']}
                                            />
                                            <Legend />
                                            <Bar dataKey="MemberCount" fill="#8884d8" name="Number of Members" />
                                        </BarChart>
                                    )}
                                </ResponsiveContainer>
                            </Box>
                        </Paper>
                    )}
                </Box>
            )}
        </Box>
    );
};

export default MemberReport;

